import STEP_LABELS from 'components/ai/aiQueryStepLabels.json';
import type {ChatMention} from 'components/DSYS/chat/chatMentions';
import type {ProfileAskKind} from 'components/profile/profileAsk';
import {getBackendUrl} from 'utilities';

/**
 * Minimal client for the AI query agent stream (POST /api/v1/ai/query/stream).
 *
 * V0 / prototype: hand-rolled NDJSON reader (mirrors the workstream context-engine
 * chat reader). Frame shapes match `helpers/ai_query/agent.py`:
 *   {type:'text', delta}
 *   {type:'tool_started', name}
 *   {type:'tool_call', id, name, args}
 *   {type:'turn', turn_id}
 *   {type:'tool_result', id, name, handle, total_found, truncated}
 *   {type:'dataset', handle, capability, items, row_count, total_found, truncated}
 *   {type:'done', result:{answer_markdown, results}}  (general agent: preview rows + row_count)
 *   {type:'error', code?, message?}
 *   {type:'speculative_expired'} | {type:'speculative_blocked', tool}  (speculative turns only)
 */

export type AiQueryToolCall = {
  /** Pairs the call with its result; absent on prefetched calls and older servers. */
  id?: string | null;
  name: string;
  args: Record<string, unknown>;
  parent?: string;
};

export type AiQueryToolResult = {
  /** The id of the call this answers (see {@link AiQueryToolCall.id}). */
  id?: string | null;
  name: string | null;
  total_found: number | null;
  truncated: boolean;
  /**
   * The model-facing row sample this call returned (capped server-side). Used
   * by the admin UI to expand the raw response for a single step.
   */
  items?: Array<Record<string, unknown>>;
  /** The dataset this call registered, when it registered one. */
  handle?: string | null;
  parent?: string;
};

export type AiQueryCapabilityResult = {
  capability: string;
  items: Array<Record<string, unknown>>;
  total_found: number | null;
  truncated: boolean;
  /**
   * Dataset-registry handle this result was registered under (e.g.
   * `find_people_1`, `combine_results_1`). Unique per result within a run, so
   * it deduplicates CSV chips when a capability or transform ran more than once.
   */
  handle?: string | null;
  /**
   * Server-marked assembled deliverable: the last transform output in the run.
   * When set, the UI uses it as the headline CSV instead of guessing from row
   * counts. Absent/false on plain capability (count_* / find_*) scratch datasets.
   */
  primary?: boolean;
  /**
   * Set when `items` is a preview (the Ask drawer's stream): the full row
   * count. The full rows come from {@link fetchAiQueryDataset} on download.
   */
  row_count?: number | null;
};

/** One ordered step of a recipe: a tool name + the (verbatim) args it ran with. */
export type AiQueryRecipeStep = {
  tool: string;
  args: Record<string, unknown>;
};

/**
 * The deterministic, replayable record of a run's tool calls. Carries no
 * results and no identity — only the steps + the run timestamp — so it is safe
 * to share; the server re-enforces permissions per replay from the requester.
 */
export type AiQueryRecipe = {
  version: number;
  as_of: string | null;
  steps: AiQueryRecipeStep[];
};

export type RecipeMode = 'live' | 'snapshot';

/** Model thinking level. Higher = more reasoning (slower) before answering. */
export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high';

export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'low';

/** Model the agent runs on. 5.5 reasons harder for complex questions; 6-sol writes App Studio apps. */
export type AiQueryModel = 'gpt-5.2' | 'gpt-5.5' | 'gpt-6-sol';
export type AiQueryAgentScope = 'general' | 'autodraft' | 'app_studio';

export const DEFAULT_MODEL: AiQueryModel = 'gpt-5.5';

/**
 * Model-authored signal that a turn could not be fully answered. `status`
 * distinguishes a useful-but-incomplete answer (`partial`) from one the agent
 * could not answer at all (`unanswerable`); `missing` is a short token naming
 * the gap. Absent/null on the `done` result means the turn was fully answered.
 */
export type AiQueryAnswerGap = {
  status: 'partial' | 'unanswerable';
  scope: 'addressable' | 'out_of_scope' | 'not_permitted';
  missing: string;
  detail: string;
};

export type AiQueryAutodraftContext = {
  workstreamId: number;
  workstreamName: string;
  currentDraft?: string;
  selectedStatus?: string;
};

export type AiQueryStudioColumnType = 'string' | 'number' | 'boolean' | 'empty' | 'mixed';

export type AiQueryStudioSampleValue = string | number | boolean | null;

/**
 * App Studio state sent each turn. A tabular upload carries only its schema and
 * a few sample rows; its full rows never leave the browser except to App
 * Platform. A text document is sent in full so the model can lay it out. The
 * current app HTML is never sent: the server reads it from the conversation.
 */
export type AiQueryStudioContext = {
  appName?: string;
  upload?: {
    fileName: string;
    rowCount: number;
    columns: {name: string; type: AiQueryStudioColumnType}[];
    sampleRows: Record<string, AiQueryStudioSampleValue>[];
  } | null;
  document?: {fileName: string; text: string} | null;
  datasets?: {handle: string; capability: string; rowCount: number; columns: string[]}[];
};

export type AiQueryResult = {
  answer_markdown: string;
  results: AiQueryCapabilityResult[];
  recipe?: AiQueryRecipe | null;
  answer_gap?: AiQueryAnswerGap | null;
};

export type AiQueryToolProgressKind = 'plan' | 'part' | 'checkpoint';

/** What a Dash search's hits are called, for its count line. */
export type AiQueryDashFoundUnit = 'message' | 'email' | 'event' | 'document' | 'result';

export type AiQueryToolProgress = {
  parent?: string;
  message: string;
  searches?: {query: string}[];
  /** Dash MCP: the app one of its calls searched, e.g. "Slack". */
  source?: string;
  /** Dash MCP: on a count line, how many hits its call returned, and what they are. */
  found?: number;
  unit?: AiQueryDashFoundUnit;
  /** Dash MCP: which call a line belongs to, so a count pairs with its search. */
  call?: number;
  /**
   * App Studio: the app's parts (`plan`, listed in `items`), the part being written
   * (`part`), or the html written since the last checkpoint (`checkpoint`, in `html`).
   */
  kind?: AiQueryToolProgressKind;
  items?: string[];
  html?: string;
};

type AiQueryFrame =
  | {type: 'conversation'; conversation_id: string}
  | {type: 'turn'; turn_id: string}
  | {type: 'text'; delta: string; parent?: string}
  | {type: 'tool_started'; name: string}
  | {
      type: 'tool_call';
      id?: string | null;
      name: string;
      args?: Record<string, unknown>;
      parent?: string;
    }
  | {
      type: 'tool_result';
      id?: string | null;
      name: string | null;
      total_found: number | null;
      truncated: boolean;
      items?: Array<Record<string, unknown>>;
      handle?: string | null;
      parent?: string;
    }
  | {
      type: 'tool_progress';
      parent?: string;
      message: string;
      searches?: {query: string}[];
      source?: string;
      found?: number;
      unit?: AiQueryDashFoundUnit;
      call?: number;
      kind?: AiQueryToolProgressKind;
      items?: string[];
      html?: string;
    }
  | {type: 'results'; results: AiQueryCapabilityResult[]}
  | {
      type: 'dataset';
      handle: string;
      capability: string;
      items: Array<Record<string, unknown>>;
      row_count: number;
      total_found: number | null;
      truncated: boolean;
    }
  | {type: 'done'; result: AiQueryResult}
  | {type: 'error'; code?: string; message?: string}
  | {type: 'speculative_expired'}
  | {type: 'speculative_blocked'; tool: string};

/** Why the server ended a speculative turn before it was sent. */
export type AiQuerySpeculativeEnd = 'expired' | 'blocked';

/**
 * Friendly, user-facing copy per server error `code`. The frontend owns UX copy,
 * so we prefer these over the server `message` (which is a safe fallback). Codes
 * mirror `_classify_query_error` in the backend's ai_query endpoint.
 */
const AI_QUERY_ERROR_MESSAGES: Record<string, string> = {
  step_limit:
    'This question needed too many steps to answer. Try narrowing it (fewer people or workstreams) or splitting it into separate questions.',
  model_error:
    'The AI service is temporarily unavailable or returned an error. This is on the service side, not your question — please try again in a moment.',
  content_flagged:
    "The AI service's security filter blocked this request. Retrying this conversation will likely be blocked again — try rephrasing, or start a new chat.",
  timeout:
    'The AI service took too long to respond. This is usually temporary — please try again, or simplify your question.',
  unknown: 'Something went wrong answering this question. Please try again.',
};

export type AiQueryStreamCallbacks = {
  onText?: (delta: string, parent?: string) => void;
  /**
   * The model began writing a tool call. onToolCall only fires once the
   * arguments are complete, which for a long argument can be most of the turn.
   */
  onToolStarted?: (name: string) => void;
  onToolCall?: (call: AiQueryToolCall) => void;
  onToolResult?: (result: AiQueryToolResult) => void;
  /**
   * The rows fetched so far, sent before `done` when a tool that renders while it's
   * written starts (App Studio previews its app with them). `done` repeats them.
   */
  onResults?: (results: AiQueryCapabilityResult[]) => void;
  /**
   * A dataset's rows (capped server-side), sent as soon as its tool finishes
   * so a `[[table:<handle>]]` marker in the answer can be drawn while it streams.
   * General agent only.
   */
  onDataset?: (dataset: AiQueryCapabilityResult) => void;
  /** Fired once per turn with the id that downloads a dataset's full rows. General agent only. */
  onTurn?: (turnId: string) => void;
  /** Fired once with the server's conversation id (reuse it on follow-ups). */
  onConversation?: (conversationId: string) => void;
  signal?: AbortSignal;
  /**
   * When true (default), capabilities bypass the actor's per-user visibility
   * gates (superuser-only tool). Set false to preview results as a normal user.
   */
  elevated?: boolean;
  /**
   * Continue a prior conversation so the agent has context for a follow-up.
   * Omit to start fresh; the server emits the new id via {@link onConversation}.
   */
  conversationId?: string;
  /** Typed editor state supplied only to the workstream autodraft agent. */
  autodraftContext?: AiQueryAutodraftContext;
  /** People the user @mentioned in this message; the server resolves each by Employee ID. */
  mentionedPeople?: ChatMention[];
  /** The Knowledge page (`/content/<category>/<id>` id) a chip's question came from. */
  knowledgePageId?: string;
  /** The profile chip the question came from (with its person as the one mention). */
  profileAsk?: ProfileAskKind;
  /** App Studio state supplied only to the app_studio agent. */
  studioContext?: AiQueryStudioContext;
  /**
   * Model thinking level. Defaults to {@link DEFAULT_REASONING_EFFORT} ('low');
   * 'none' is faster, 'high' for harder questions.
   */
  reasoningEffort?: ReasoningEffort;
  /**
   * Model the agent runs on. Defaults to {@link DEFAULT_MODEL} ('gpt-5.5');
   * 'gpt-5.2' is faster and weaker.
   */
  model?: AiQueryModel;
  enableSourcegraph?: boolean;
  /** Use the minimal workstream update agent instead of the general Ask agent. */
  agentScope?: AiQueryAgentScope;
  onToolProgress?: (progress: AiQueryToolProgress) => void;
  /**
   * Start the turn before the user sends it (global search, while they type).
   * The server keeps it out of history and analytics, and runs no write tools,
   * until {@link adoptSpeculativeAiQuery}; an unsent turn ends with the stream.
   */
  speculative?: boolean;
  /**
   * The answer, as soon as it arrives. A speculative turn's stream stays open
   * after it until the turn is sent, so the returned promise settles later.
   */
  onDone?: (result: AiQueryResult) => void;
  /** A speculative turn ended unsent (timed out, or reached a write tool). */
  onSpeculativeEnd?: (reason: AiQuerySpeculativeEnd) => void;
};

/**
 * Internal run-signal tools that must not surface in the user-facing trace.
 * `report_answer_gap` only records that the agent could not fully answer (see
 * the `answer_gap` on the done result); showing it as a "step" would confuse
 * users, so its tool_call/tool_result frames are dropped before callbacks fire.
 */
/** Run signals, not steps (e.g. report_answer_gap). */
const HIDDEN_TOOL_NAMES = new Set<string>(STEP_LABELS.hidden);

export const getAiQueryHttpErrorMessage = (
  payload: unknown,
  fallback = 'Request failed'
): string => {
  if (!payload || typeof payload !== 'object' || !('detail' in payload)) {
    return fallback;
  }
  const detail = (payload as {detail?: unknown}).detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (!Array.isArray(detail)) {
    return fallback;
  }

  const tooLong = detail.find(
    (item): item is {type: string; loc?: unknown[]} =>
      Boolean(item) &&
      typeof item === 'object' &&
      (item as {type?: unknown}).type === 'string_too_long'
  );
  if (tooLong) {
    return tooLong.loc?.includes('current_draft')
      ? 'This draft is too long for Ask. Shorten it and try again.'
      : 'This message is too long for Ask. Shorten it and try again.';
  }
  return 'We could not send this request. Check the input and try again.';
};

export const sanitizeAiQueryProgressMessage = (message: string): string =>
  message
    .replace(/\s*\[Context:[^\]]*\]\s*/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

export const filterAiQueryToolProgress = (
  progress: AiQueryToolProgress,
  seen: Set<string>
): AiQueryToolProgress | null => {
  const message = sanitizeAiQueryProgressMessage(progress.message);
  if (!message || /^show thinking\b/i.test(message)) {
    return null;
  }
  const normalized = {...progress, message};
  // Kinded progress is state (the plan, the part Ask is on), not a message to show once.
  // Dash MCP lines are one per call: two pages of "20 messages" are both real.
  if (normalized.kind || normalized.source) {
    return normalized;
  }
  const fingerprint = JSON.stringify([
    normalized.parent?.toLowerCase() ?? '',
    message.toLowerCase(),
    normalized.searches?.map(({query}) => query.trim().toLowerCase()) ?? [],
  ]);
  if (seen.has(fingerprint)) {
    return null;
  }
  seen.add(fingerprint);
  return normalized;
};

function handleFrame(
  frame: AiQueryFrame,
  callbacks: AiQueryStreamCallbacks,
  onDone: (r: AiQueryResult) => void,
  seenProgress: Set<string>
): void {
  switch (frame.type) {
    case 'conversation':
      if (frame.conversation_id) {
        callbacks.onConversation?.(frame.conversation_id);
      }
      return;
    case 'turn':
      if (frame.turn_id) {
        callbacks.onTurn?.(frame.turn_id);
      }
      return;
    case 'text':
      if (frame.delta) {
        callbacks.onText?.(frame.delta, frame.parent);
      }
      return;
    case 'tool_started':
      if (!HIDDEN_TOOL_NAMES.has(frame.name)) {
        callbacks.onToolStarted?.(frame.name);
      }
      return;
    case 'tool_call':
      if (HIDDEN_TOOL_NAMES.has(frame.name)) {
        return;
      }
      callbacks.onToolCall?.({
        id: frame.id,
        name: frame.name,
        args: frame.args ?? {},
        parent: frame.parent,
      });
      return;
    case 'tool_result':
      if (frame.name && HIDDEN_TOOL_NAMES.has(frame.name)) {
        return;
      }
      callbacks.onToolResult?.({
        id: frame.id,
        name: frame.name,
        total_found: frame.total_found,
        truncated: frame.truncated,
        items: frame.items,
        handle: frame.handle,
        parent: frame.parent,
      });
      return;
    case 'dataset':
      callbacks.onDataset?.({
        capability: frame.capability,
        items: frame.items,
        total_found: frame.total_found,
        truncated: frame.truncated,
        handle: frame.handle,
        row_count: frame.row_count,
      });
      return;
    case 'tool_progress': {
      const progress = filterAiQueryToolProgress(
        {
          parent: frame.parent,
          message: frame.message,
          searches: frame.searches,
          source: frame.source,
          found: frame.found,
          unit: frame.unit,
          call: frame.call,
          kind: frame.kind,
          items: frame.items,
          html: frame.html,
        },
        seenProgress
      );
      if (!progress) {
        return;
      }
      callbacks.onToolProgress?.(progress);
      return;
    }
    case 'results':
      callbacks.onResults?.(frame.results ?? []);
      return;
    case 'done':
      onDone(frame.result);
      callbacks.onDone?.(frame.result);
      return;
    case 'speculative_expired':
      callbacks.onSpeculativeEnd?.('expired');
      return;
    case 'speculative_blocked':
      callbacks.onSpeculativeEnd?.('blocked');
      return;
    case 'error': {
      const friendly =
        (frame.code && AI_QUERY_ERROR_MESSAGES[frame.code]) ||
        frame.message?.trim() ||
        AI_QUERY_ERROR_MESSAGES.unknown;
      throw new Error(friendly);
    }
    default:
      return;
  }
}

export async function streamAiQuery(
  question: string,
  callbacks: AiQueryStreamCallbacks = {}
): Promise<AiQueryResult> {
  const base = getBackendUrl().replace(/\/+$/, '');
  const res = await fetch(`${base}/api/v1/ai/query/stream`, {
    method: 'POST',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      question,
      mentioned_people: callbacks.mentionedPeople?.length
        ? callbacks.mentionedPeople.map(({employee_id, name}) => ({employee_id, name}))
        : undefined,
      knowledge_page_id: callbacks.knowledgePageId,
      profile_ask: callbacks.profileAsk,
      autodraft_context: callbacks.autodraftContext
        ? {
            workstream_id: callbacks.autodraftContext.workstreamId,
            workstream_name: callbacks.autodraftContext.workstreamName,
            current_draft: callbacks.autodraftContext.currentDraft,
            selected_status: callbacks.autodraftContext.selectedStatus,
          }
        : undefined,
      studio_context: callbacks.studioContext
        ? {
            app_name: callbacks.studioContext.appName,
            upload: callbacks.studioContext.upload
              ? {
                  file_name: callbacks.studioContext.upload.fileName,
                  row_count: callbacks.studioContext.upload.rowCount,
                  columns: callbacks.studioContext.upload.columns,
                  sample_rows: callbacks.studioContext.upload.sampleRows,
                }
              : undefined,
            document: callbacks.studioContext.document
              ? {
                  file_name: callbacks.studioContext.document.fileName,
                  text: callbacks.studioContext.document.text,
                }
              : undefined,
            datasets: callbacks.studioContext.datasets?.map((dataset) => ({
              handle: dataset.handle,
              capability: dataset.capability,
              row_count: dataset.rowCount,
              columns: dataset.columns,
            })),
          }
        : undefined,
      elevated: callbacks.elevated ?? true,
      conversation_id: callbacks.conversationId,
      reasoning_effort: callbacks.reasoningEffort ?? DEFAULT_REASONING_EFFORT,
      model: callbacks.model ?? DEFAULT_MODEL,
      enable_sourcegraph: callbacks.enableSourcegraph ?? false,
      agent_scope: callbacks.agentScope ?? 'general',
      speculative: callbacks.speculative || undefined,
    }),
    signal: callbacks.signal,
  });

  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const j: unknown = await res.json();
      detail = getAiQueryHttpErrorMessage(j, detail);
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  const body = res.body;
  if (!body) {
    throw new Error('Empty response body');
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result: AiQueryResult | null = null;
  const seenProgress = new Set<string>();
  const onDone = (r: AiQueryResult) => {
    result = r;
  };

  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }
    let frame: AiQueryFrame;
    try {
      frame = JSON.parse(trimmed) as AiQueryFrame;
    } catch {
      throw new Error('Invalid stream data from server');
    }
    handleFrame(frame, callbacks, onDone, seenProgress);
  };

  let streamDone = false;
  while (!streamDone) {
    const {done, value} = await reader.read();
    streamDone = done;
    buf += value ? decoder.decode(value, {stream: !done}) : '';
    let nl = buf.indexOf('\n');
    while (nl >= 0) {
      processLine(buf.slice(0, nl));
      buf = buf.slice(nl + 1);
      nl = buf.indexOf('\n');
    }
  }
  if (buf.trim()) {
    processLine(buf);
  }

  if (!result) {
    throw new Error('Stream ended without a result');
  }
  return result;
}

/**
 * Send a speculative turn: from here the server saves and records it like any
 * other. `leadMs` is how long it had run, the time the early start saved. False
 * when the turn already ended unsent; ask normally then.
 */
export async function adoptSpeculativeAiQuery(
  conversationId: string,
  leadMs: number
): Promise<boolean> {
  const base = getBackendUrl().replace(/\/+$/, '');
  const res = await fetch(`${base}/api/v1/ai/query/speculative/adopt`, {
    method: 'POST',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      conversation_id: conversationId,
      lead_ms: Math.max(0, Math.round(leadMs)),
    }),
  });
  return res.ok;
}

/**
 * Replay a captured recipe deterministically (no model) under the current user.
 *
 * The server re-runs the same capabilities + transforms, re-enforcing the
 * requester's permissions on every step. `mode: 'snapshot'` pins the recipe's
 * run time as an upper bound on append-only/event data (events created after
 * are excluded; existing records still reflect current values).
 */
export async function runRecipe(
  recipe: AiQueryRecipe,
  mode: RecipeMode,
  opts: {signal?: AbortSignal; elevated?: boolean} = {}
): Promise<AiQueryCapabilityResult[]> {
  const base = getBackendUrl().replace(/\/+$/, '');
  const res = await fetch(`${base}/api/v1/ai/query/run-recipe`, {
    method: 'POST',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({recipe, mode, elevated: opts.elevated ?? false}),
    signal: opts.signal,
  });

  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const j: unknown = await res.json();
      if (j && typeof j === 'object' && 'detail' in j) {
        const d = (j as {detail?: unknown}).detail;
        detail = typeof d === 'string' ? d : JSON.stringify(d);
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  const json = (await res.json()) as {results?: AiQueryCapabilityResult[]};
  return json.results ?? [];
}

/**
 * The full rows of one dataset from an Ask turn (headline table or any step),
 * for CSV download. The server serves what the turn cached, or replays
 * `recipe` up to `handle` once that has expired. Pass the `elevated` the turn
 * streamed with so a replay sees the same rows (like `runRecipe`, it defaults
 * to off).
 */
export async function fetchAiQueryDataset(args: {
  turnId: string;
  handle: string;
  recipe?: AiQueryRecipe | null;
  elevated?: boolean;
  signal?: AbortSignal;
}): Promise<AiQueryCapabilityResult> {
  const base = getBackendUrl().replace(/\/+$/, '');
  const res = await fetch(`${base}/api/v1/ai/query/dataset`, {
    method: 'POST',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      turn_id: args.turnId,
      handle: args.handle,
      recipe: args.recipe ?? null,
      elevated: args.elevated ?? false,
    }),
    signal: args.signal,
  });
  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      // ignore
    }
    throw new Error(getAiQueryHttpErrorMessage(payload, 'Download failed'));
  }
  const json = (await res.json()) as {result: AiQueryCapabilityResult};
  return json.result;
}

/**
 * Max encoded-recipe length we will put in a shareable URL. Beyond this a link
 * risks hitting browser/proxy URL-length limits, so the UI offers "Copy recipe"
 * (JSON, paste-to-run) instead.
 */
export const MAX_RECIPE_LINK_LENGTH = 4000;

// UTF-8 <-> latin1 "binary string" conversions so btoa/atob handle non-ASCII
// (e.g. accented names, emoji in args). Uses only universally-available
// primitives (btoa/atob/encode/decodeURIComponent) — no TextEncoder/escape — so
// it behaves identically in browsers and the jsdom test environment.
const utf8ToBinary = (str: string): string =>
  encodeURIComponent(str).replace(/%([0-9A-Fa-f]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

const binaryToUtf8 = (binary: string): string => {
  let percent = '';
  for (let i = 0; i < binary.length; i += 1) {
    percent += `%${binary.charCodeAt(i).toString(16).padStart(2, '0')}`;
  }
  return decodeURIComponent(percent);
};

/** URL-safe, UTF-8-safe base64 of a recipe, for embedding in a shareable link. */
export const encodeRecipe = (recipe: AiQueryRecipe): string =>
  btoa(utf8ToBinary(JSON.stringify(recipe)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/** Inverse of {@link encodeRecipe}; throws on malformed input. */
export const decodeRecipe = (encoded: string): AiQueryRecipe =>
  JSON.parse(binaryToUtf8(atob(encoded.replace(/-/g, '+').replace(/_/g, '/')))) as AiQueryRecipe;

// ---------------------------------------------------------------------------
// Generic tool approval
// ---------------------------------------------------------------------------

export type ApprovalPreviewField = {label: string; value: string};

export type ApprovalMetadata = {
  token: string;
  execute_tool: string;
  execute_args: Record<string, unknown>;
  preview_title: string;
  preview_subtitle?: string | null;
  /** Label/value rows for the card (To, When, Invites). */
  preview_fields?: ApprovalPreviewField[];
  /** The text the action writes. */
  preview_body?: string | null;
  /** The execute_args key holding preview_body, when the card lets the user edit it. */
  body_field?: string | null;
  warning?: string | null;
};

/**
 * Execute a user-approved action and stream the agent continuation turn.
 *
 * Mirrors `streamAiQuery` but POSTs to the `/approve` endpoint. Fires the same
 * callback shapes so the caller can append the continuation to the conversation.
 */
export async function streamApproveAction(
  conversationId: string,
  token: string,
  toolName: string,
  args: Record<string, unknown>,
  callbacks: AiQueryStreamCallbacks = {}
): Promise<AiQueryResult> {
  const base = getBackendUrl().replace(/\/+$/, '');
  const res = await fetch(`${base}/api/v1/ai/query/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      conversation_id: conversationId,
      token,
      tool_name: toolName,
      args,
      continue_chat: true,
    }),
    signal: callbacks.signal,
  });

  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const j: unknown = await res.json();
      if (j && typeof j === 'object' && 'detail' in j) {
        const d = (j as {detail?: unknown}).detail;
        detail = typeof d === 'string' ? d : JSON.stringify(d);
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  const body = res.body;
  if (!body) {
    throw new Error('Empty response body');
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result: AiQueryResult | null = null;
  const seenProgress = new Set<string>();
  const onDone = (r: AiQueryResult) => {
    result = r;
  };

  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }
    let frame: AiQueryFrame;
    try {
      frame = JSON.parse(trimmed) as AiQueryFrame;
    } catch {
      throw new Error('Invalid stream data from server');
    }
    handleFrame(frame, callbacks, onDone, seenProgress);
  };

  let streamDone = false;
  while (!streamDone) {
    const {done, value} = await reader.read();
    streamDone = done;
    buf += value ? decoder.decode(value, {stream: !done}) : '';
    let nl = buf.indexOf('\n');
    while (nl >= 0) {
      processLine(buf.slice(0, nl));
      buf = buf.slice(nl + 1);
      nl = buf.indexOf('\n');
    }
  }
  if (buf.trim()) {
    processLine(buf);
  }

  if (!result) {
    throw new Error('Stream ended without a result');
  }
  return result;
}
